import axios from '@/plugins/axios';
import ErrorHandle from '@/helpers/errors/errorHandle';
import downloadFile from '@/helpers/downloadFile';
import enumerateArray from '@/helpers/enumerateArray';

const initialState = {
  list: [],
  currentPage: 1,
  lastPage: 1,
  perPage: 25,
  paymentTypes: [],
  investorInfo: {},
  investorCars: [],
  investorInfoBalance: 0,
  investorInfoPayment: 0,
  investorInfoDeposit: 0,
  investorInfoHistory: [],
};

const actions = {
  async fetchList({ commit }, params = {}) {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      const { data } = await axios.get('investors-report', {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
        params,
      });

      const list = enumerateArray(
        data.data.investors.items.data,
        data.data.investors.items.current_page,
        data.data.investors.items.per_page,
      );

      commit('setList', list);
      commit('setCurrentPage', data.data.investors.items.current_page);
      commit('setLastPage', data.data.investors.items.last_page);
      commit('setPerPage', Number(data.data.investors.items.per_page));

      return data;
    } catch (error) {
      return ErrorHandle(error, this);
    }
  },

  async fetchPaymentTypes({ commit }) {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      const { data } = await axios.get('investors/payment-types', {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      const list = Object.values(data.data).map((name, id) => ({ name, id }));
      commit('setPaymentTypes', list);

      return data;
    } catch (error) {
      return ErrorHandle(error, this);
    }
  },

  async fetchInvestorInfo({ state, commit }, id) {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      const { data } = await axios.get(`investors-report/${id}`, {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      const list = [...state.list];
      const currentIdx = list.findIndex((item) => item.id === id);

      if (currentIdx !== -1) {
        list[currentIdx] = data.data;
        commit('setList', list);
      }

      commit('setInvestorInfo', data.data);

      return data;
    } catch (error) {
      return ErrorHandle(error, this);
    }
  },

  async updateItem(_, { id, params }) {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      await axios.put(`investors/${id}`, params, {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      return { success: true };
    } catch {
      return { error: true };
    }
  },

  async addTransaction(_, { id, params }) {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      await axios.post(`investors/${id}/transactions`, params, {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      // await dispatch('fetchInvestorInfo', id);

      return { success: true };
    } catch (error) {
      return { error: true };
    }
  },

  async editTransaction({ dispatch }, { id, params }) {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      await axios.put(`investors/${id}/transactions`, params, {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      await dispatch('fetchInvestorInfo', id);

      return { success: true };
    } catch (error) {
      return { error: true };
    }
  },

  async fetchInvestorCars({ commit }, { id, params }) {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      const { data } = await axios.get(`investor_board/${id}`, {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
        params,
      });

      commit('setInvestorCars', enumerateArray(data.data.items.items));
      commit('setInvestorInfoHistory', data.data.transactions);
      commit('setInvestorInfoCounters', {
        balance: data.data.balance,
        payment: data.data.payment,
        deposit: data.data.deposit,
      });

      return data;
    } catch (error) {
      return ErrorHandle(error, this);
    }
  },

  async downloadReport(_, { params, name }) {
    try {
      const lang = localStorage.getItem('language') || '';
      const token = localStorage.getItem('token') || '';
      const { data } = await axios.get('investors-report', {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
        params: {
          ...params,
          export: 1,
        },
      });

      downloadFile(data, name);

      return { success: true };
    } catch {
      return { error: true };
    }
  },
};

const mutations = {
  setList(state, payload) {
    state.list = payload;
  },

  setCurrentPage(state, payload) {
    state.currentPage = payload;
  },

  setLastPage(state, payload) {
    state.lastPage = payload;
  },

  setPerPage(state, payload) {
    state.perPage = payload;
  },

  setPaymentTypes(state, payload) {
    state.paymentTypes = payload;
  },

  setInvestorInfo(state, payload) {
    state.investorInfo = payload;
  },

  setInvestorCars(state, payload) {
    state.investorCars = payload;
  },

  setInvestorInfoCounters(state, { balance, payment, deposit }) {
    state.investorInfoBalance = balance;
    state.investorInfoPayment = payment;
    state.investorInfoDeposit = deposit;
  },

  setInvestorInfoHistory(state, payload) {
    state.investorInfoHistory = payload;
  },
};

export default {
  namespaced: true,
  state: initialState,
  actions,
  mutations,
};
